import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, primeCsrfToken } from "@/api/client";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Prime CSRF cookie + axios cache before POSTs (same source as GET /api/csrf-token).
 */
async function primeCsrf() {
  await primeCsrfToken();
}

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token")?.trim() || "";

  const pendingVerifyEmail =
    typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem("pending_verify_email") || ""
      : "";

  const [phase, setPhase] = useState(
    token ? "loading" : "no-token"
  );
  const [resendEmail, setResendEmail] = useState(() =>
    typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem("pending_verify_email") || ""
      : ""
  );
  const [resendLoading, setResendLoading] = useState(false);
  const [resendError, setResendError] = useState("");
  const [showResendInput, setShowResendInput] = useState(false);
  const [showChangeEmailInfo, setShowChangeEmailInfo] = useState(false);

  useEffect(() => {
    primeCsrfToken().catch(() => {});
  }, []);

  const runVerify = useCallback(async (verifyToken) => {
    setPhase("loading");
    try {
      await primeCsrf();
      await api.auth.verifyEmail(verifyToken);
      setPhase("success");
    } catch (err) {
      const status = err.response?.status;
      const code = err.response?.data?.error;
      if (status === 400 && (code === "INVALID_TOKEN" || code)) {
        setPhase("error");
        return;
      }
      setPhase("error");
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    runVerify(token);
  }, [token, runVerify]);

  const handleResend = async (e) => {
    e?.preventDefault?.();
    const email = resendEmail.trim();
    if (!email) {
      setResendError("Enter your email address.");
      return;
    }
    setResendError("");
    setResendLoading(true);
    try {
      await primeCsrfToken();
      await api.auth.resendVerification({ email });
      setPhase("resent");
    } catch (err) {
      const code = err.response?.data?.error;
      if (code === "ALREADY_VERIFIED") {
        setResendError("This email is already verified. You can log in.");
      } else {
        setResendError(
          err.response?.data?.error || "Could not send email. Try again."
        );
      }
    } finally {
      setResendLoading(false);
    }
  };

  if (phase === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="flex flex-col items-center gap-4 text-center max-w-md">
          <Loader2 className="h-10 w-10 animate-spin text-indigo-600" />
          <p className="text-gray-600 font-medium">Verifying your email...</p>
        </div>
      </div>
    );
  }

  if (phase === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md bg-white rounded-lg shadow-md p-8 text-center">
          <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            Email verified successfully
          </h1>
          <p className="text-ad-muted mb-6">
            You can now log in to Agent David.
          </p>
          <Button
            className="w-full"
            onClick={() => navigate("/login")}
          >
            Go to login →
          </Button>
        </div>
      </div>
    );
  }

  if (phase === "resent") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md bg-white rounded-lg shadow-md p-8 text-center">
          <p className="text-ad-text mb-2">
            New link sent to{" "}
            <span className="font-medium">{resendEmail.trim()}</span>
          </p>
          <p className="text-sm text-ad-muted mb-6">
            Check your inbox and use the new link within 24 hours.
          </p>
          <Button variant="outline" onClick={() => navigate("/login")}>
            Back to login
          </Button>
        </div>
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md bg-white rounded-lg shadow-md p-8">
          <div className="text-center mb-6">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
            <h1 className="text-lg font-bold text-gray-900 mb-2">
              This verification link has expired
            </h1>
            <p className="text-sm text-ad-muted">
              Verification links expire after 24 hours.
            </p>
          </div>

          <form onSubmit={handleResend} className="space-y-4">
            <p className="text-sm font-medium text-gray-600">
              Request a new link
            </p>
            <input
              type="email"
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
              required
            />
            {resendError && (
              <p className="text-sm text-red-600">{resendError}</p>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={resendLoading}
            >
              {resendLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2 inline" />
                  Sending…
                </>
              ) : (
                "Request a new link"
              )}
            </Button>
          </form>
          <Button
            variant="ghost"
            className="w-full mt-4"
            onClick={() => navigate("/login")}
          >
            Back to login
          </Button>
        </div>
      </div>
    );
  }

  /* no-token */
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-12">
      <div className="w-full max-w-md bg-white rounded-lg shadow-md p-8">
        <h1 className="text-xl font-bold text-gray-900 text-center mb-2">
          Check your email
        </h1>
        <p className="text-ad-muted text-center mb-1">
          We sent a verification link to:
        </p>
        {pendingVerifyEmail ? (
          <p className="text-center font-medium text-gray-900 mb-2 break-all">
            {pendingVerifyEmail}
          </p>
        ) : (
          <p className="text-ad-muted text-center mb-2">
            We sent a verification link to your email.
          </p>
        )}
        <p className="text-sm text-ad-muted text-center mb-8">
          Click the link in the email to verify your account.
        </p>

        <div className="space-y-4">
          {!showResendInput ? (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                setShowResendInput(true);
                setResendError("");
              }}
            >
              Resend verification email
            </Button>
          ) : (
            <form onSubmit={handleResend} className="space-y-3">
              <input
                type="email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="Your email"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                required
              />
              {resendError && (
                <p className="text-sm text-red-600">{resendError}</p>
              )}
              <Button type="submit" className="w-full" disabled={resendLoading}>
                {resendLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                ) : (
                  "Send new link"
                )}
              </Button>
            </form>
          )}

          {!showChangeEmailInfo ? (
            <Button
              variant="ghost"
              className="w-full text-ad-muted"
              onClick={() => setShowChangeEmailInfo(true)}
            >
              Change email address
            </Button>
          ) : (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <p className="font-medium mb-1">To change your email</p>
              <p>
                To change your email contact support — the self-service change
                flow is not available yet.
              </p>
            </div>
          )}
        </div>

        <Button
          variant="link"
          className="w-full mt-6"
          onClick={() => navigate("/login")}
        >
          Back to login
        </Button>
      </div>
    </div>
  );
}
